import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import clouds from 'assets/images/import-clouds.svg'
import { OpenPositionFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { Authorized } from 'modules/core/components/Authorized'
import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { FormErrors } from 'modules/core/hooks/useForm'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateReferral } from 'modules/ats/hooks/referrals/useCreateReferral'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

interface ReferralFormValues {
  firstName: string
  lastName: string
  atsJobPostingId: string
  email: string
  phone?: string
  note?: string
  personalUrl?: string
  cv?: File
  cvOrPersonalUrl?: string
}

interface Props {
  openPosition: OpenPositionFragment
  onClose: () => void
}

const ReferCandidateModalComponent = ({ openPosition, onClose }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const handleSubmitForm = async () => {
    const fields: string[] = Object.keys(initialValues)
    await fields.forEach((field: string) => setFieldTouched(field, true, true))
    handleSubmit()
  }

  const { createReferral } = useCreateReferral()
  const {
    values,
    setValues,
    isSubmitting,
    errors,
    setErrors,
    handleSubmit,
    isValid,
    touched,
    setFieldTouched,
    initialValues,
  } = useForm<ReferralFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      personalUrl: '',
      atsJobPostingId: openPosition.id,
    },
    validate: (data) => {
      const errors: FormErrors<ReferralFormValues> = {}

      if (!data.firstName && touched.firstName) {
        errors.firstName = i18n.t('common.mandatory')
      }
      if (!data.lastName && touched.lastName) {
        errors.lastName = i18n.t('common.mandatory')
      }
      if (!data.email && touched.email) {
        errors.email = i18n.t('common.mandatory')
      }
      if (!data.cv && !data.personalUrl && touched.personalUrl) {
        errors.cvOrPersonalUrl = i18n.t('ats.open_positions.refer_modal.cv_or_personal_url')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await createReferral(values)

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
        return
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.open_positions.refer_modal.success_message'),
        })

        onClose()
      }
      return handled
    },
  })

  const isButtonDisable = !isValid || isSubmitting

  const onUploadedFile = (files: File[]) => {
    setValues({
      ...values,
      cv: files[0],
    })
  }

  const onRemoveFile = () => {
    setValues({
      ...values,
      cv: undefined,
    })
  }

  return (
    <Modal
      illustration="id"
      title={i18n.t('ats.open_positions.refer_modal.title_label', {
        jobTitle: openPosition.title,
      })}
      description={i18n.t('ats.open_positions.refer_modal.description')}
      onClose={onClose}
      onSubmit={() => handleSubmitForm()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={isButtonDisable} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Box gap="s16" flexDirection="row">
          <TextField
            label={i18n.t('ats.open_positions.refer_modal.first_name')}
            value={values.firstName}
            error={errors.firstName}
            mandatory
            onChange={(value) =>
              setValues({
                ...values,
                firstName: value,
              })
            }
            onBlur={() => setFieldTouched('firstName', true, true)}
          />
          <TextField
            label={i18n.t('ats.open_positions.refer_modal.last_name')}
            value={values.lastName}
            error={errors.lastName}
            mandatory
            onChange={(value) =>
              setValues({
                ...values,
                lastName: value,
              })
            }
            onBlur={() => setFieldTouched('lastName', true, true)}
          />
        </Box>
        <TextField
          label={i18n.t('ats.open_positions.refer_modal.email')}
          value={values.email}
          error={errors.email}
          onChange={(value) =>
            setValues({
              ...values,
              email: value,
            })
          }
          mandatory
          onBlur={() => setFieldTouched('email', true, true)}
        />
        <TextField
          label={i18n.t('ats.open_positions.refer_modal.phone')}
          value={values.phone}
          onChange={(value) =>
            setValues({
              ...values,
              phone: value,
            })
          }
        />
        <TextArea
          label={i18n.t('ats.open_positions.refer_modal.note')}
          value={values.note}
          onChange={(value) =>
            setValues({
              ...values,
              note: value,
            })
          }
        />

        <Text.Normal weight="medium" color="grey1000">
          {i18n.t('ats.open_positions.refer_modal.provide_attachments')}
          {errors.cvOrPersonalUrl && (
            <Text.Normal size="200" color="radicalRed1000">
              {errors.cvOrPersonalUrl}
            </Text.Normal>
          )}
        </Text.Normal>

        <TextField
          label={i18n.t('ats.open_positions.refer_modal.personal_url')}
          value={values.personalUrl}
          onChange={(value) =>
            setValues({
              ...values,
              personalUrl: value,
            })
          }
          onBlur={() => setFieldTouched('personalUrl', true, true)}
        />
        <Box gap="s4">
          <Text.Normal size="200" weight="medium">
            CV
          </Text.Normal>
          {values.cv ? (
            <Box
              border={{
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
              borderRadius={{ all: 'abs008' }}
              paddingX="s24"
              paddingY="s16"
            >
              <Box
                alignItems="center"
                justifyContent="spaceBetween"
                flexGrow
                flexDirection="row"
                gap="s16"
              >
                <Box flexDirection="row" alignItems="center" gap="s16">
                  <DocumentType mimetype={values.cv.type} />
                  <Box overflowX="hidden" maxWidth="s240">
                    <Text.Normal weight="medium" ellipsis overflowWrap>
                      {values.cv.name}
                    </Text.Normal>
                  </Box>
                </Box>
                <Button type="secondary" onClick={() => onRemoveFile()} iconName="delete" />
              </Box>
            </Box>
          ) : (
            <Authorized subscription={subscription}>
              <DropzoneBox
                onDrop={onUploadedFile}
                icon={clouds}
                multiple={false}
                message={i18n.t('documents.drop_here')}
                accept={acceptedContentTypes()}
              />
            </Authorized>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export const ReferCandidateModal = observer(ReferCandidateModalComponent)
